import { QaReplayPanel } from "./QaReplayPanel.js";
import { EnvironmentSetupResult } from "../settings/EnvironmentConfigSummary.js";
import { EnvironmentHumanSignIn } from "../settings/EnvironmentHumanSignIn.js";
import { QaEvidencePack, type QaReport } from "./QaEvidencePack.js";
import { useEffect, useRef, useState } from "react";
import { Markdown } from "../components/Markdown.js";
import { Button, Chip, Pane } from "../ui/index.js";
import { captureUiError } from "../sentry.js";
import type { CaseFileProps } from "./caseBodyProps.js";

type Section = "result" | "feedback" | "decisions" | "revisions";
type Provenance = {
  userId: string;
  credentialId: string;
  channel: string;
  action: string;
  targetVersion: string;
};
type Summary = {
  workItemId?: string;
  teamId: string;
  requestId: string;
  worker: string;
  status: string;
  inputVersion: string;
  resultVersion: string | null;
  createdAt: string;
  headSha?: string | null;
  error?: string | null;
};
type Decision = {
  id: string;
  kind: string;
  status: string;
  version: string;
  payload: {
    type: string;
    wording?: string;
    body?: string;
    worker: string;
    teamId: string;
    repo?: string;
    prNumber?: number;
    headSha?: string;
    resultVersion: string;
  };
  resolution?: {
    action?: string;
    provenance?: Provenance;
    publicationAuthorization?: Provenance | null;
    effect?: { htmlUrl?: string | null };
  } | null;
};
type WorkResponse = Summary & {
  progress?: {
    stage: string;
    interactiveSessionId?: string;
    captures: import("@yagni/shared").QaEvidence[];
  };
  result?: {
    qa?: QaReport;
    environmentSetup?: import("@yagni/shared").QaSetupReport;
    markdown: string;
    findings: Array<{
      id: string;
      title: string;
      detail: string;
      severity?: string;
      path?: string;
      line?: number;
      evidence?: string;
    }>;
    evidence: Array<{ kind: string; reference: string }>;
    limitations: string[];
    nextOffset: number | null;
    nextMarkdownOffset: number | null;
  } | null;
  input?: {
    mode?: "environment_setup" | "replay_validation";
    repoId?: string;
    replayId?: string;
    replayHash?: string;
    repository?: string;
    prNumber?: number;
    planPreview?: string;
  };
  cost?: { sellMillicents: number };
  feedback?: Array<{
    id: string;
    body: string;
    created_at?: string;
    metadata: { findingId?: string; resultVersion?: string };
  }>;
  decisions?: Decision[];
  revisions?: Summary[];
  nextOffset?: number | null;
};
const labels: Record<Section, string> = {
  result: "Result",
  feedback: "Feedback",
  decisions: "Decisions",
  revisions: "Revisions",
};

function reviewReceiptUrl(decision: Decision): string | null {
  const value = decision.resolution?.effect?.htmlUrl;
  if (!value || !decision.payload.repo || !decision.payload.prNumber)
    return null;
  try {
    const url = new URL(value);
    return url.origin === "https://github.com" &&
      !url.username &&
      !url.password &&
      url.pathname ===
        `/${decision.payload.repo}/pull/${decision.payload.prNumber}` &&
      !url.search &&
      /^#pullrequestreview-\d+$/.test(url.hash)
      ? url.href
      : null;
  } catch {
    return null;
  }
}

function DecisionRecord({
  decision,
  deskName,
  workerOf,
  onResolve,
  busy,
}: {
  decision: Decision;
  onResolve: (decision: Decision, action: "accept" | "decline") => void;
  busy: boolean;
} & Pick<CaseFileProps, "deskName" | "workerOf">) {
  const payload = decision.payload;
  const instruction = payload.type === "worker_instruction";
  const provenance = decision.resolution?.provenance;
  const receiptUrl = reviewReceiptUrl(decision);
  return (
    <Pane
      material={decision.status === "open" ? "staged" : "printed"}
      className="min-w-0 space-y-3 break-words p-4"
    >
      <h3 className="font-semibold">
        {instruction ? "Instruction change" : "GitHub comment review"} ·{" "}
        {decision.status}
      </h3>
      <p>
        {workerOf?.(payload.worker)?.displayName ?? payload.worker} · {deskName}
        {payload.repo
          ? ` · ${payload.repo}`
          : " · All permitted Team repositories"}
      </p>
      {instruction ? (
        <>
          <p>
            {decision.status === "open"
              ? "Inactive until you accept this exact wording."
              : "The resolution below records whether this instruction was accepted."}
          </p>
          <p className="whitespace-pre-wrap break-words">{payload.wording}</p>
        </>
      ) : (
        <>
          <p>
            Publishes this exact comment on {payload.repo} #{payload.prNumber}{" "}
            at commit {payload.headSha}.
          </p>
          <p>
            This Decision does not approve, request changes, or merge the pull
            request.
          </p>
          <Markdown text={payload.body ?? ""} />
        </>
      )}
      {decision.status === "open" && (
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => onResolve(decision, "accept")}>
            {instruction ? "Accept instruction" : "Publish this exact comment"}
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => onResolve(decision, "decline")}
          >
            Decline
          </Button>
        </div>
      )}
      {provenance && (
        <div className="space-y-1 text-sm">
          <p>
            {provenance.action === "accept" ? "Accepted" : "Declined"} through{" "}
            {provenance.channel === "mcp"
              ? "the connected coding client; a delegated action"
              : "the web app"}
            .
          </p>
        </div>
      )}
      {receiptUrl && (
        <p>
          <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
            View published review on GitHub
          </a>
        </p>
      )}
      <details className="qa-fold">
        <summary>Decision versions and provenance</summary>
        <div className="qa-fold-sub space-y-1">
          <p>
            Agent {payload.worker} · Team {payload.teamId}
          </p>
          <p>
            Decision {decision.id} · {decision.version}
          </p>
          <p>Result {payload.resultVersion}</p>
          {provenance && (
            <p>
              Responsible user {provenance.userId} · credential{" "}
              {provenance.credentialId} · channel {provenance.channel} · action{" "}
              {provenance.action} · reviewed version {provenance.targetVersion}
            </p>
          )}
          {decision.resolution?.publicationAuthorization && (
            <p>
              Publication authorized by user{" "}
              {decision.resolution.publicationAuthorization.userId} through{" "}
              {decision.resolution.publicationAuthorization.channel}, credential{" "}
              {decision.resolution.publicationAuthorization.credentialId}.
            </p>
          )}
        </div>
      </details>
    </Pane>
  );
}

function ResultSection({
  view,
  markdownOffset,
  onMarkdownPage,
  onAuthenticationComplete,
}: {
  view: WorkResponse;
  markdownOffset: number;
  onMarkdownPage: (offset: number) => void;
  onAuthenticationComplete: (sessionId: string) => void;
}) {
  const result = view.result;
  const [qaSelection, setQaSelection] = useState<{
    id: string;
    nonce: number;
  }>();
  const nextMarkdownOffset = result?.nextMarkdownOffset;
  return (
    <>
      {view.input?.repository && (
        <p className="wrc-fact">
          {view.input.repository}
          {view.input.prNumber ? ` #${view.input.prNumber}` : ""}
          {view.headSha ? ` · ${view.headSha}` : ""}
        </p>
      )}
      {view.input?.planPreview && (
        <details className="qa-fold">
          <summary>Submitted plan preview</summary>
          <p className="whitespace-pre-wrap break-words">
            {view.input.planPreview}
          </p>
          <p className="text-sm">
            The first 1,000 characters of this immutable input snapshot.
          </p>
        </details>
      )}
      {view.progress && (
        <div role="status">
          <p>{view.progress.stage}</p>
          {view.progress.captures?.at(-1) && (
            <img
              className="wrc-progress-shot"
              src={view.progress.captures.at(-1)!.url}
              alt="Latest captured browser view"
            />
          )}
        </div>
      )}
      {view.status === "running" && view.progress?.interactiveSessionId && (
        <EnvironmentHumanSignIn
          key={view.progress.interactiveSessionId}
          sessionId={view.progress.interactiveSessionId}
          onResumed={() =>
            onAuthenticationComplete(view.progress!.interactiveSessionId!)
          }
          onStopped={() =>
            onAuthenticationComplete(view.progress!.interactiveSessionId!)
          }
          retryMessage="Request a new check to start a new session."
        />
      )}
      {result ? (
        <>
          {/* Historical only: nothing creates a setup request any more, so
              this renders the record and no longer offers a page to accept it
              on. "Review repository environment" below is the way back. */}
          {result.environmentSetup && (
            <EnvironmentSetupResult report={result.environmentSetup} />
          )}
          {result.qa && (
            <>
              <QaEvidencePack
                key={view.requestId}
                report={result.qa}
                headSha={view.headSha}
                selection={qaSelection}
              />
              {result.qa.replayProposals?.map((replay) => (
                <QaReplayPanel key={replay.id} replay={replay} />
              ))}
              {result.qa.replayValidation &&
                view.resultVersion &&
                result.qa.replayChecks?.map((replay) => (
                  <QaReplayPanel
                    key={replay.replayId}
                    replay={{
                      id: replay.replayId,
                      name: replay.name,
                      definitionHash: replay.definitionHash,
                    }}
                    validation={{
                      requestId: view.requestId,
                      resultVersion: view.resultVersion!,
                      passed: replay.passed && result.qa?.outcome === "passed",
                    }}
                  />
                ))}
            </>
          )}
          <Pane material="printed" className="p-4 wrc-prose">
            <Markdown text={result.markdown} />
          </Pane>
          {(markdownOffset > 0 || nextMarkdownOffset != null) && (
            <div className="flex flex-wrap gap-2">
              {markdownOffset > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onMarkdownPage(Math.max(0, markdownOffset - 8000))
                  }
                >
                  Previous result text
                </Button>
              )}
              {nextMarkdownOffset != null && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onMarkdownPage(nextMarkdownOffset)}
                >
                  More result text
                </Button>
              )}
            </div>
          )}
          <section className="wrc-sect">
            <h2 className="wrc-sect-l">Findings</h2>
            {result.findings.map((finding) => (
              <Pane
                key={finding.id}
                material="printed"
                className="space-y-2 p-4"
              >
                <h3 className="wrc-finding-title">{finding.title}</h3>
                <div className="wrc-prose">
                  <Markdown text={finding.detail} />
                </div>
                {result.qa?.captures.some(
                  (c) => c.kind === "screenshot" && c.url === finding.evidence,
                ) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      const capture = result.qa?.captures.find(
                        (c) =>
                          c.kind === "screenshot" && c.url === finding.evidence,
                      );
                      if (capture)
                        setQaSelection({ id: capture.id, nonce: Date.now() });
                    }}
                  >
                    View evidence
                  </Button>
                )}
                {finding.evidence?.startsWith("/api/qa/evidence/") &&
                  !result.qa?.captures.some(
                    (c) => c.url === finding.evidence,
                  ) && (
                    <p className="wrc-empty">
                      Evidence is unavailable on this result.
                    </p>
                  )}
                <p className="wrc-finding-meta">
                  {finding.id}
                  {finding.path
                    ? ` · ${finding.path}${finding.line ? `:${finding.line}` : ""}`
                    : ""}
                  {finding.severity ? ` · ${finding.severity}` : ""}
                </p>
              </Pane>
            ))}
            {!result.findings.length && (
              <p className="wrc-empty">No findings recorded on this page.</p>
            )}
          </section>
          <section className="wrc-sect">
            <h2 className="wrc-sect-l">Evidence</h2>
            {result.evidence.length ? (
              <ul className="wrc-list">
                {result.evidence.map((evidence, index) => (
                  <li key={index}>{evidence.reference}</li>
                ))}
              </ul>
            ) : (
              <p className="wrc-empty">No evidence references recorded.</p>
            )}
          </section>
          <section className="wrc-sect">
            <h2 className="wrc-sect-l">Limitations</h2>
            {result.limitations.length ? (
              <ul className="wrc-list">
                {result.limitations.map((limitation, index) => (
                  <li key={index}>{limitation}</li>
                ))}
              </ul>
            ) : (
              <p className="wrc-empty">No limitations recorded.</p>
            )}
          </section>
        </>
      ) : (
        <p className="wrc-empty">
          {view.status === "failed"
            ? `The Agent could not complete this request${view.error ? ` (${view.error})` : ""}. Request an explicit recheck in your coding client.`
            : "The Agent is still preparing its result. You can leave and return to this record."}
        </p>
      )}
      {view.cost && (
        <p className="wrc-fact">
          Run cost ${(view.cost.sellMillicents / 100000).toFixed(4)}
        </p>
      )}
    </>
  );
}

export function WorkerRequestCaseFile(props: CaseFileProps) {
  return <WorkerRequestBody key={props.item.id} {...props} />;
}

function WorkerRequestBody({
  item,
  deskName,
  workerOf,
  onNext,
}: CaseFileProps) {
  const [acting, setActing] = useState(false);
  const viewGeneration = useRef(0),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      viewGeneration.current++;
    };
  }, []);
  const [feedback, setFeedback] = useState("");
  const operationKeys = useRef(new Map<string, string>());
  const [section, setSection] = useState<Section>("result");
  const [requestId, setRequestId] = useState<string>();
  const pinnedRequest = useRef<string | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [markdownOffset, setMarkdownOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [resumedSessionId, setResumedSessionId] = useState<string>();
  const [view, setView] = useState<WorkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    viewGeneration.current++;
    setView(null);
    setError(null);
    const query = new URLSearchParams({
      section,
      offset: String(offset),
      markdownOffset: String(markdownOffset),
    });
    const selectedRequest = requestId ?? pinnedRequest.current;
    if (selectedRequest) query.set("requestId", selectedRequest);
    fetch(`/api/worker-requests/${encodeURIComponent(item.id)}?${query}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok)
          throw new Error(
            response.status === 404
              ? "This Agent record is unavailable to your current workspace or Team."
              : "The Agent record could not be loaded. Try again.",
          );
        return response.json() as Promise<WorkResponse>;
      })
      .then((value) => {
        if (!controller.signal.aborted) {
          pinnedRequest.current = value.requestId;
          setView(value);
        }
      })
      .catch((failure: unknown) => {
        if (controller.signal.aborted) return;
        captureUiError(new Error("Worker case file read failed"), {
          area: "Work",
          operation: "read_worker_request_case_file",
        });
        setError(
          failure instanceof Error
            ? failure.message
            : "The Agent record could not be loaded.",
        );
      });
    return () => {
      viewGeneration.current++;
      controller.abort();
    };
  }, [item.id, section, requestId, offset, markdownOffset, refresh]);
  useEffect(() => {
    if (view?.status !== "queued" && view?.status !== "running") return;
    if (
      view.progress?.interactiveSessionId &&
      view.progress.interactiveSessionId !== resumedSessionId
    )
      return;
    const timer = setTimeout(() => setRefresh((value) => value + 1), 15_000);
    return () => clearTimeout(timer);
  }, [view, resumedSessionId]);
  async function act(action: string, input: Record<string, unknown>) {
    const startedGeneration = viewGeneration.current;
    const identity = JSON.stringify({ action, ...input });
    let key = operationKeys.current.get(identity);
    if (!key) {
      key = crypto.randomUUID();
      operationKeys.current.set(identity, key);
    }
    setActing(true);
    setError(null);
    try {
      const response = await fetch(`/api/worker-requests/actions/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...input, operationKey: key }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error ?? "The action failed.");
      if (!mounted.current || startedGeneration !== viewGeneration.current)
        return false;
      if (action === "test-pr" || action === "validate-qa-replay") {
        pinnedRequest.current = data.requestId;
        setRequestId(data.requestId);
        setSection("result");
      }
      setRefresh((n) => n + 1);
      return true;
    } catch (failure) {
      if (!mounted.current || startedGeneration !== viewGeneration.current)
        return false;
      setError(
        failure instanceof Error ? failure.message : "The action failed.",
      );
      captureUiError(failure, { area: "Work", operation: "worker_decision" });
      return false;
    } finally {
      if (mounted.current) setActing(false);
    }
  }
  function selectSection(next: Section) {
    viewGeneration.current++;
    setSection(next);
    setOffset(0);
    setMarkdownOffset(0);
  }
  const nextOffset =
    section === "result" ? view?.result?.nextOffset : view?.nextOffset;
  return (
    <article
      className="tri-read wk-read cf-read wrc-read min-w-0 break-words p-4 md:p-6"
      aria-label="Agent request case file"
    >
      <header className="wrc-head">
        <p className="cf-byline">
          <span className="cf-byline-team">{deskName}</span>
          {view && (
            <span className="cf-byline-fact">
              {workerOf?.(view.worker)?.displayName ?? view.worker}
            </span>
          )}
        </p>
        <div className="cf-head-row">
          <h1 className="cf-title">{item.title}</h1>
          {view && (
            <span className="cf-head-pill">
              <Chip>{view.status}</Chip>
            </span>
          )}
        </div>
        <p className="wrc-disclosure">
          No automatic external publication. {view?.worker === "fletcher"
            ? "Access follows this record’s Team and repository permissions."
            : "This record has normal Team visibility in YAGNI."} Any GitHub
          publication is recorded as a separate Decision.
        </p>
        <div className="wrc-actions">
          {view?.status === "completed" &&
            view.resultVersion &&
            view.input?.mode !== "environment_setup" &&
            (view.worker === "proctor" || view.worker === "fletcher") && (
              <Button
                size="sm"
                disabled={acting}
                onClick={async () => {
                  if (
                    await act("prepare-publication", {
                      requestId: view.requestId,
                      expectedVersion: view.resultVersion,
                    })
                  )
                    selectSection("decisions");
                }}
              >
                Prepare GitHub comment for approval
              </Button>
            )}
          {view && <FletcherActions view={view} acting={acting} act={act} />}
        </div>
        <div className="wrc-utils">
          <button type="button" onClick={onNext}>
            Next ticket
          </button>
          <span className="wrc-utils-sep" aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => setRefresh((value) => value + 1)}
          >
            Refresh
          </button>
          <span className="wrc-utils-sep" aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => {
              pinnedRequest.current = undefined;
              setRequestId(undefined);
              selectSection("result");
              setRefresh((value) => value + 1);
            }}
          >
            Read latest revision
          </button>
        </div>
      </header>
      <nav
        aria-label="Agent record sections"
        className="cf-mission-tabs wrc-tabs"
      >
        {(Object.keys(labels) as Section[]).map((key) => (
          <button
            key={key}
            type="button"
            className={section === key ? "is-active" : ""}
            aria-pressed={section === key}
            onClick={() => selectSection(key)}
          >
            {labels[key]}
          </button>
        ))}
      </nav>
      {error && <p role="alert" className="wrc-empty">{error}</p>}
      {!view && !error && (
        <p role="status" className="wrc-empty">
          Loading Agent record…
        </p>
      )}
      {view && (
        <>
          {section === "result" && (
            <ResultSection
              view={view}
              markdownOffset={markdownOffset}
              onMarkdownPage={setMarkdownOffset}
              onAuthenticationComplete={(sessionId) => {
                if (resumedSessionId === sessionId) return;
                setResumedSessionId(sessionId);
                setRefresh((value) => value + 1);
              }}
            />
          )}
          {section === "feedback" && (
            <section className="space-y-3">
              <h2 className="font-semibold">Feedback on this revision</h2>
              {view.status === "completed" && (
                <form
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (
                      await act("feedback", {
                        requestId: view.requestId,
                        expectedVersion: view.resultVersion,
                        body: feedback,
                      })
                    )
                      setFeedback("");
                  }}
                >
                  <label>
                    Correction or disagreement
                    <textarea
                      required
                      className="block w-full border p-2"
                      value={feedback}
                      onChange={(event) => setFeedback(event.target.value)}
                    />
                  </label>
                  <Button disabled={acting || !feedback.trim()} type="submit">
                    Record feedback
                  </Button>
                </form>
              )}
              {view.feedback?.map((feedback) => (
                <Pane
                  key={feedback.id}
                  material="printed"
                  className="space-y-2 p-4"
                >
                  <Markdown text={feedback.body} />
                  <p className="text-sm break-all">
                    {feedback.metadata.findingId
                      ? `Finding ${feedback.metadata.findingId}`
                      : "Result correction"}
                    {feedback.metadata.resultVersion
                      ? ` · ${feedback.metadata.resultVersion}`
                      : ""}
                  </p>
                </Pane>
              ))}
              {!view.feedback?.length && (
                <p>No feedback recorded on this page.</p>
              )}
            </section>
          )}
          {section === "decisions" && (
            <section className="space-y-3">
              <h2 className="font-semibold">Decisions on this work record</h2>
              {view.decisions?.map((decision) => (
                <DecisionRecord
                  key={decision.id}
                  decision={decision}
                  deskName={deskName}
                  workerOf={workerOf}
                  busy={acting}
                  onResolve={(selected, action) =>
                    void act("resolve-decision", {
                      decisionId: selected.id,
                      expectedVersion: selected.version,
                      action,
                    })
                  }
                />
              ))}
              {!view.decisions?.length && (
                <p>No Decisions recorded on this page.</p>
              )}
            </section>
          )}
          {section === "revisions" && (
            <section className="space-y-3">
              <h2 className="font-semibold">Immutable request revisions</h2>
              {view.revisions?.map((revision) => (
                <Pane
                  key={revision.requestId}
                  material="printed"
                  className="space-y-2 p-4"
                >
                  <p>
                    {revision.createdAt} ·{" "}
                    {workerOf?.(revision.worker)?.displayName ??
                      revision.worker}{" "}
                    · {revision.status}
                  </p>
                  <details className="qa-fold">
                    <summary>Request identifier</summary>
                    <p className="qa-fold-sub">{revision.requestId}</p>
                  </details>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setRequestId(revision.requestId);
                      selectSection("result");
                    }}
                  >
                    Read this revision
                  </Button>
                </Pane>
              ))}
            </section>
          )}
          <div className="flex flex-wrap gap-2">
            {offset > 0 && (
              <Button
                variant="ghost"
                onClick={() =>
                  setOffset(
                    Math.max(0, offset - (section === "decisions" ? 1 : 5)),
                  )
                }
              >
                Previous {labels[section].toLowerCase()} page
              </Button>
            )}
            {nextOffset != null && (
              <Button variant="secondary" onClick={() => setOffset(nextOffset)}>
                More{" "}
                {section === "result"
                  ? "findings"
                  : labels[section].toLowerCase()}
              </Button>
            )}
          </div>
          <details className="qa-fold">
            <summary>Request and result versions</summary>
            <div className="qa-fold-sub space-y-1">
              <p>Request {view.requestId}</p>
              <p>Input {view.inputVersion}</p>
              <p>Result {view.resultVersion ?? "Pending"}</p>
            </div>
          </details>
        </>
      )}
    </article>
  );
}

function FletcherActions({
  view,
  acting,
  act,
}: {
  view: WorkResponse;
  acting: boolean;
  act: (action: string, input: Record<string, unknown>) => Promise<boolean>;
}) {
  const recheck =
    view.input?.mode === "replay_validation"
      ? {
          action: "validate-qa-replay",
          input: {
            replayId: view.input.replayId,
            expectedDefinitionHash: view.input.replayHash,
            previousRequestId: view.requestId,
          },
          label: "Request replay recheck",
        }
      : {
          action: "test-pr",
          input: {
            teamId: view.teamId,
            repo: view.input?.repository,
            prNumber: view.input?.prNumber,
            previousRequestId: view.requestId,
          },
          label: "Request paid recheck",
        };
  return (
    <>
      {view.worker === "fletcher" &&
        ["completed", "failed"].includes(view.status) &&
        view.input?.repository &&
        view.input.prNumber && (
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={acting}
              onClick={() => void act(recheck.action, recheck.input)}
            >
              {recheck.label}
            </Button>
            {view.result?.limitations.some((text) =>
              text.includes("fork PR"),
            ) &&
              view.resultVersion && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={acting}
                  onClick={() =>
                    void act("authorize-fork", {
                      requestId: view.requestId,
                      expectedVersion: view.resultVersion,
                    })
                  }
                >
                  Authorize this commit to use test credentials
                </Button>
              )}
            <a
              className="wrc-link-btn"
              href={
                view.input.repoId
                  ? `/services/repositories?repo=${encodeURIComponent(view.input.repoId)}`
                  : "/services/repositories"
              }
            >
              Review repository environment
            </a>
          </>
        )}
    </>
  );
}
